import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from '../../hooks';
import { FullPageLoader } from '../shared';
import { Card } from '../ui/card';
import { QuestionTypeBadge } from '../assignments/QuestionTypeBadge';
import ReadingComprehensionLayout from '../assignments/AssignmentQuestion/ReadingComprehensionLayout';
import MCQReview from './MCQReview';
import FIBReview from './FIBReview';
import SubjectiveReview from './SubjectiveReview';
import CodeReview from './CodeReview';
import { QUESTION_TYPES } from '../../constants';
import FileUploadReview from './FileUploadReview';
import AudioReview from './AudioReview';
import VideoReview from './VideoReview';
import { QuestionResultTag } from './QuestionResultTag';
import { getEffectiveChoiceGroup } from '../../utils/choiceGroups';
import { getReviewQuestionUrl } from '../../utils/urlHelpers';

const ReviewQuestionAnswer = (props) => {
  const {
    question = {},
    questionResponse = {},
    isLoading,
    answerScoreData,
    answerStatus,
    rcContext,
    assignmentData
  } = props;
  const { id, type } = question;
  const { questionId, studentAssignmentId } = useParams();
  const navigate = useNavigate();

  if (isLoading) {
    return <FullPageLoader />;
  }

  const rcRef = question.readingComprehensionQuestionRef;
  const hasRcLayout = Boolean(rcContext && rcRef);
  const reviewQuestion = hasRcLayout
    ? { ...question, readingComprehensionQuestionRef: null }
    : question;

  const handleRcJump = (subQuestionId) => {
    navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: subQuestionId }));
  };

  const reviewBody = (
    <>
      {type === QUESTION_TYPES.MCQ && (
        <MCQReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.FIB && (
        <FIBReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.SUBJECTIVE && (
        <SubjectiveReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.FILE_UPLOAD && (
        <FileUploadReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.AUDIO && (
        <AudioReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.VIDEO && (
        <VideoReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
      {type === QUESTION_TYPES.CODE && (
        <CodeReview
          question={reviewQuestion}
          response={questionResponse}
          answerScoreData={answerScoreData}
          answerStatus={answerStatus}
          key={question?.id}
        />
      )}
    </>
  );

  // Inside an "attempt any X of these Y" block, only the student's best X answers earned
  // marks. Without this note a correct answer showing 0 marks reads as a grading mistake.
  // Its own group, or its passage's when the whole block was the alternative - either way
  // that is what decided whether this answer earned anything.
  const choiceGroup = getEffectiveChoiceGroup(assignmentData?.assignment, id);
  const isCountedInGroup = answerScoreData?.countedInChoiceGroup;

  const questionCard = (
    <Card className="border border-border/50 p-4 shadow-sm md:p-6">
      {choiceGroup && (
        <div
          className={`mb-4 rounded-lg border px-3 py-2 text-xs ${
            isCountedInGroup
              ? 'border-purple-200 bg-purple-50 text-purple-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <span className="font-medium">
            Attempt any {choiceGroup.requiredCount} of {(choiceGroup.questions || []).length}
          </span>{' '}
          —{' '}
          {isCountedInGroup
            ? `this answer is one of the ${choiceGroup.requiredCount} that counted.`
            : `your best ${choiceGroup.requiredCount} answers counted, and this was not one of them.`}
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <QuestionTypeBadge type={type} />
        <QuestionResultTag
          answerStatus={answerStatus}
          score={answerScoreData?.score}
          maxScore={answerScoreData?.maxScore ?? question?.score}
          className="md:max-w-[70%] md:text-right"
        />
      </div>
      {reviewBody}
    </Card>
  );

  return id ? (
    <div className="w-full" key={questionId}>
      {hasRcLayout ? (
        <ReadingComprehensionLayout
          readingComprehensionQuestionRef={rcRef}
          rcContext={rcContext}
          assignmentData={assignmentData}
          onJump={handleRcJump}
        >
          {questionCard}
        </ReadingComprehensionLayout>
      ) : (
        <div className="px-3 pb-3 md:px-4 md:pb-4">{questionCard}</div>
      )}
    </div>
  ) : null;
};

export default ReviewQuestionAnswer;
